import { ContextMenuHandler, type IMenuItem } from "@asup/context-menu";
import React, { useState } from "react";
import { REACT_APP_STAGE } from "../../../config";
import { getScaffoldContextMenuTarget } from "../../scaffold/getScaffoldContextMenuTarget";
import { getFromDataPath } from "../../utility";
import { isGroupingFactor } from "../typeGuards";
import { useArsTable } from "./ArsTableContextProvider";
import { cellActionConfig } from "./cellActionConfig";
import { arst_resolveTargetPath } from "./functions";
import { ARST_SET_VIEW_ITEM, ArsTableCellData, ArsTableState, ArsTableViewItem } from "./interface";
import { isArsTableGroupCalculationDto } from "./typeguards";

/**
 * Props for ArsTableContextMenuProvider.
 */
export interface ArsTableContextMenuProviderProps {
  /** Scaffold content to render inside the provider boundary. */
  children: React.ReactNode;
}

export interface ArsTableContextMenuTarget<T> {
  colId: string;
  rowId: string;
  value: ArsTableCellData<T>;
}

/**
 * Resolves a view-item payload from a scaffold cell when the target is supported.
 *
 * @param cellValue - Current scaffold cell value
 * @param state - Current ArsTableContext state
 * @returns View-item payload for the properties panel, or undefined when unsupported
 */
const resolveViewItem = <T,>(
  cellValue: ArsTableCellData<T>,
  state: ArsTableState<T>,
): ArsTableViewItem | undefined => {
  // istanbul ignore next - typeGuard that has already been validated before this call
  if (!("targetId" in cellValue)) return undefined;

  /** For groups and dataValues return the groupingFactor */
  if (cellValue.targetType === "group" || cellValue.targetType === "dataDriven") {
    const targetDataPath = arst_resolveTargetPath(cellValue.dataPath);
    const groupingFactor = getFromDataPath(
      `${targetDataPath}/groupingFactor`,
      state.bodyData,
      (obj) => isGroupingFactor(obj),
    );
    /* istanbul ignore else */
    if (groupingFactor) {
      return {
        id: groupingFactor.id,
        type: "groupingFactor",
        dataPath: targetDataPath,
      };
    } else return undefined;
  }
  return {
    id: cellValue.targetId,
    type: cellValue.targetType,
    dataPath: cellValue.dataPath,
  };
};

/**
 * Builds sorted context-menu labels for developer inspection groups.
 *
 * @param obj - Source object used to build display rows
 * @returns Context-menu items for developer display
 */
// istanbul ignore next -- developer-only inspection helper is intentionally excluded from coverage gate
const getDeveloperMenuItems = (obj?: object): IMenuItem[] =>
  Object.entries(obj ?? {})
    .filter(
      ([key]) => !["analysisMethod", "groupingFactor", "operation", "displayContent"].includes(key),
    )
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([key, val]) => ({
      label: (
        <div>
          <span style={{ fontWeight: "bold" }}>{`${key}`}</span>
          {`: ${val}`}
        </div>
      ),
    }));

/**
 * Context-menu wrapper for ArsTable scaffold and cell content.
 * Resolves table-level actions when no scaffold cell is targeted and
 * cell-level actions by deriving row/column ids from the right-click event target.
 *
 * @param props - Wrapper props
 * @param props.children - Child scaffold content
 * @returns Wrapped scaffold content
 */
export const ArsTableContextMenuProvider = <T,>({
  children,
}: ArsTableContextMenuProviderProps): React.ReactElement => {
  const { state, dispatch } = useArsTable<T>();
  const [contextMenuTarget, setContextMenuTarget] = useState<
    ArsTableContextMenuTarget<T> | undefined
  >(undefined);
  if (!state) return <>{children}</>;

  /**
   * Function to blur active element
   */
  const blurActiveElement = (): void => {
    /* istanbul ignore else */
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  /**
   * Captures scaffold row and column ids before opening the context menu.
   *
   * @param event - Context menu event fired from scaffold cells
   */
  const handleScaffoldContextMenuCapture = (event: React.MouseEvent<HTMLDivElement>): void => {
    let ret: ArsTableContextMenuTarget<T> | undefined = undefined;
    const target = getScaffoldContextMenuTarget(event.target);
    /* istanbul ignore else */
    if (target) {
      const resolvedValue = state?.scaffoldModel?.cellData?.[target.rowId]?.[target.colId];
      /* istanbul ignore else */
      if (resolvedValue) {
        ret = { ...target, value: resolvedValue };
      }
    }

    setContextMenuTarget(ret);
  };

  const actionList: IMenuItem[] = [];

  if (contextMenuTarget) {
    if ("targetId" in contextMenuTarget.value && contextMenuTarget.value.targetType) {
      actionList.push({
        label: "Show item properties",
        action: () => {
          blurActiveElement();
          const viewItem = resolveViewItem(contextMenuTarget.value, state);
          /* istanbul ignore else */
          if (viewItem) {
            dispatch({ operation: ARST_SET_VIEW_ITEM, viewItem });
          }
        },
      });
    }
    const cellActions = cellActionConfig(contextMenuTarget.value, state).contextMenuActions.map(
      (actionDefn) => ({
        label: actionDefn.label,
        disabled: actionDefn.disabled,
        action: () => {
          blurActiveElement();
          dispatch({ operation: actionDefn.operation, cellValue: contextMenuTarget.value });
        },
      }),
    );
    actionList.push(...cellActions);

    /** Add developer info display */
    // istanbul ignore next
    if (
      ["local", "development", "test", "__REACT_APP_STAGE__"].includes(REACT_APP_STAGE) &&
      state.bodyData
    ) {
      const developerMenuItems: IMenuItem[] = [
        {
          label: "Developer info",
          group: getDeveloperMenuItems(contextMenuTarget.value),
        },
      ];
      actionList.push(...developerMenuItems);
      const targetDataPath = arst_resolveTargetPath(contextMenuTarget.value.dataPath);
      const dataPathData = getFromDataPath(targetDataPath, state.bodyData, (obj) =>
        isArsTableGroupCalculationDto(obj, state.settings.editorProps.isT),
      );
      if (dataPathData) {
        const developerGroupMenuItems: IMenuItem[] = [
          { label: "Developer info (group)", group: getDeveloperMenuItems(dataPathData) },
        ];
        if ("groupingFactor" in dataPathData) {
          developerGroupMenuItems[0].group?.push(
            ...getDeveloperMenuItems(dataPathData.groupingFactor),
          );
        }
        if ("analysisMethod" in dataPathData) {
          developerGroupMenuItems[0].group?.push(
            ...getDeveloperMenuItems(dataPathData.analysisMethod),
          );
        }
        if ("operation" in dataPathData) {
          developerGroupMenuItems[0].group?.push(...getDeveloperMenuItems(dataPathData.operation));
        }
        actionList.push(...developerGroupMenuItems);
      }
    }
  }

  return (
    <ContextMenuHandler
      menuItems={actionList}
      onContextMenuCapture={handleScaffoldContextMenuCapture}
    >
      {children}
    </ContextMenuHandler>
  );
};

ArsTableContextMenuProvider.displayName = "ArsTableContextMenuProvider";
